import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { getPackageOptions, getPackageQueryKey, updateTemplateMutation } from '@drivingdoc/api-client/generated/react-query';
import { GetPackageResult } from 'types/packages';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import EmailTemplateForm, { EmailTemplateOutputValues } from 'components/TemplateForms';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface EmailTemplateProps {
  packageId: string;
}

export function WelcomeLetter({ packageId }: EmailTemplateProps) {
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const updateEmailTemplate = useMutation(updateTemplateMutation());

  // fetch the package
  const pkg = useQuery({
    ...getPackageOptions({
      path: {
        id: packageId,
      },
    }),
    enabled: !!packageId,
  });
  const packageData = pkg.data as GetPackageResult | undefined;

  const onSubmit = async (data: EmailTemplateOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm(`Update welcome letter for "${packageData?.name}"?`, {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateEmailTemplate.mutateAsync({
        path: {
          templateKey: 'welcome-letter',
          id: packageId,
        },
        body: {
          email: data,
        },
      });

      if (result) {
        // close the dialog
        // handleClose();

        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: getPackageQueryKey({
            path: {
              id: packageId,
            },
          }),
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Welcome Letter Updated',
        });
      }

      // const editorState = editor.getEditorState();
      // console.log(editorState.toJSON());
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    }
  };
  return (
    <RichTextEditorProvider>
      <EmailTemplateForm templateKey="welcome-letter" id={packageId} onSubmit={onSubmit} />
    </RichTextEditorProvider>
  );
}

export default WelcomeLetter;
